<template>
  <div class="sf-page">
    <v-progress-linear color="accent"
                       indeterminate
                       v-bind:active="loading" />
    <div v-if="supplier != null">
      <v-snackbar color="success"
                  right
                  top
                  v-model="confirmationSnackbar">
        {{ $t('saved') }}
      </v-snackbar>
      <confirm-dialog ref="deleteDialog"
                      v-bind:cancel-button-label="$t('cancel')"
                      v-bind:confirm-button-label="$t('remove')"
                      v-bind:message="$t('confirmDeleteSupplier')"
                      v-bind:title="$t('remove')"
                      v-model="deleteDialog"
                      v-on:confirm="remove" />
      <top-header v-bind:subtitle="supplier.id"
                  v-bind:title="supplier.companyName | defaultValue($t('untitledSupplier'))">
        <v-btn class="sf-back-btn"
               exact
               slot="left-button"
               text
               v-bind:to="{ name: 'administration-suppliers' }">
          <v-icon left>ti-arrow-left</v-icon>
          {{ $t('back') }}
        </v-btn>
        <template slot="right-button">
          <v-btn color="primary"
                 outlined
                 v-bind:loading="exportLoading"
                 v-on:click="exportCsv">
            <v-icon left>ti-file-spreadsheet</v-icon>
            {{ $t('exportOrders') }}
          </v-btn>
          <v-btn color="primary"
                 depressed
                 exact
                 v-bind:disabled="!hasChanges"
                 v-on:click="updateSupplier">
            <v-icon left>ti-check</v-icon>
            {{ $t('save') }}
          </v-btn>
        </template>
        <v-menu offset-y
                left
                slot="menu">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon
                   v-bind="attrs"
                   v-bind:title="$t('actions')"
                   v-on="on">
              <v-icon>ti-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item v-on:click="deleteDialog = true">
              <v-list-item-icon class="mr-3"><v-icon color="error">ti-trash</v-icon></v-list-item-icon>
              <v-list-item-title class="error--text">{{ $t('remove') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-tabs class="sf-tabs"
                slot="tabs"
                v-model="tabs">
          <v-tab href="#common">{{ $t('common') }}</v-tab>
          <v-tab href="#design">{{ $t('design') }}</v-tab>
        </v-tabs>
      </top-header>
      <div class="sf-container">
        <v-alert class="sf-alert"
                 text
                 type="error"
                 v-model="validationAlert">
          {{ $t('pleaseCompleteTheForm') }}
        </v-alert>
        <v-tabs-items v-model="tabs">
          <v-tab-item value="common">
            <v-row>
              <v-col cols="12"
                     lg="6">
                <supplier-edit-card ref="supplierEditCard"
                                    v-bind:edit-mode="true"
                                    v-bind:supplier="supplier" />
              </v-col>
              <v-col cols="12"
                     lg="6">
                <supplier-invitation-edit-card ref="supplierInvitationEditCard"
                                               v-bind:supplier="supplier" />
              </v-col>
            </v-row>
          </v-tab-item>
          <v-tab-item value="design">
            <design-card v-bind:form="form"
                         v-bind:products="designProducts"
                         v-bind:supplier="supplier" />
          </v-tab-item>
        </v-tabs-items>
      </div>
    </div>
  </div>
</template>

<script>
  import SupplierEditCard from '@/components/administration/SupplierEditCard'
import ConfirmDialog from '@/components/common/ConfirmDialog'
  import SupplierInvitationEditCard from '@/components/administration/SupplierInvitationEditCard'
  import TopHeader from '@/components/common/TopHeader'
  import DesignCard from '@/components/registration/DesignCard'

  export default {
    async beforeRouteUpdate(to, from, next) {
      const id = to.params.id

      await this.get(id)

      next()
    },
    components: {
      ConfirmDialog,
      DesignCard,
      SupplierEditCard,
      SupplierInvitationEditCard,
      TopHeader
    },
    async created() {
      const id = this.$route.params.id

      await this.get(id)
    },
    data: () => ({
      confirmationSnackbar: false,
      deleteDialog: false,
      designProducts: [],
      form: {},
      hasChanges: false,
      id: null,
      supplier: null,
      loading: false,
      tabs: null,
      validationAlert: false,
      exportLoading: false
    }),
    methods: {
      async remove() {
        await this.$store.dispatch('deleteSupplier', { id: this.id })

        this.$router.push({ name: 'administration-suppliers' })
      },
      async get(id) {
        this.id = id

        await this.$store.dispatch('loadSuppliers')

        this.supplier = Object.assign({}, this.$store.getters.supplier(id))
        this.designProducts = await this.$store.dispatch('loadProductsForDesignPreview', { supplierId: id })
      },
      async exportCsv() {
        this.exportLoading = true

        const csv = await this.$store.dispatch('exportOrdersForSupplier', { supplierId: this.id })

        let element = document.createElement('a')
        element.setAttribute('href', 'data:text/csv;charset=utf-8,%EF%BB%BF' + encodeURI(csv))
        element.setAttribute('download', 'export.csv')
        element.style.display = 'none'

        document.body.appendChild(element)

        element.click();

        document.body.removeChild(element)

        this.exportLoading = false
      },
      async updateSupplier() {
        if (!this.$refs.supplierEditCard.validate() || !this.$refs.supplierInvitationEditCard.validate()) {
          this.validationAlert = true
          this.$vuetify.goTo(0)

          return
        }

        await this.$store.dispatch('updateSupplier', { supplier: this.supplier })

        this.confirmationSnackbar = true
        this.hasChanges = false
      }
    },
    name: 'Supplier',
    watch: {
      supplier: {
        deep: true,
        handler(newSupplier, oldSupplier) {
          if (newSupplier == null || oldSupplier == null) {
            this.hasChanges = false
          } else if (newSupplier.id !== oldSupplier.id) {
            this.hasChanges = false
          } else {
            this.hasChanges = true
          }
        }
      }
    }
  }
</script>

<i18n>
  {
    "de": {
      "back": "Zurück",
      "common": "Allgemein",
      "design": "Design",
      "exportOrders": "Bestellungen exportieren",
      "pleaseCompleteTheForm": "Bitte vervollständigen Sie Ihre Angaben.",
      "save": "Speichern",
      "saved": "Gespeichert.",
      "untitledSupplier": "Unbenannter Lieferant",
      "actions": "Aktionen",
      "cancel": "Abbrechen",
      "remove": "Löschen",
      "confirmDeleteSupplier": "Möchten Sie diesen Lieferanten endgültig löschen? Dies kann nicht rückgängig gemacht werden."
    },
    "en": {
      "back": "Back",
      "common": "Common",
      "design": "Design",
      "exportOrders": "Export orders",
      "pleaseCompleteTheForm": "Please complete the form.",
      "save": "Save",
      "saved": "Saved.",
      "untitledSupplier": "Unitled supplier",
      "actions": "Actions",
      "cancel": "Cancel",
      "remove": "Delete",
      "confirmDeleteSupplier": "Do you really want to delete this supplier? This cannot be undone."
    }
  }
</i18n>